import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'

import * as components from './components'
import { AtnGlFilterStore } from './store'

@ngCore.Component({
  imports: [
    ds.illustration.emptyState.Component,
    components.comparison.Component,
    components.statement.Component,
  ],
  providers: [
    // Based on: https://stackoverflow.com/questions/67711813/angular-providers-use-existing-or-create-one-if-not-provided
    {
      // If a parent filter already has a store, we want to reuse that.
      // Otherwise, we create a new store
      provide: AtnGlFilterStore,
      useFactory: () => {
        const existingStore = ngCore.inject(AtnGlFilterStore, {
          optional: true,
          skipSelf: true,
        })
        return existingStore ?? new AtnGlFilterStore()
      },
    },
  ],
  selector: 'atn-rate-gl-filter',
  standalone: true,
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component implements ngCore.OnInit {
  /* ------------------------------- Injections ------------------------------- */

  /** @see {@link AtnGlFilterStore} */
  public readonly store = ngCore.inject(AtnGlFilterStore)

  /**
   * Used in {@link ngOnInit}
   */
  private readonly _injector: ngCore.Injector = ngCore.inject(ngCore.Injector)

  /* --------------------------------- Inputs --------------------------------- */

  /**
   * {@link ents.extra.rate.gl.Filter} being rendered
   */
  public readonly filterSg = ngCore.input(
    {},
    {
      alias: 'filter',
      transform: (filter?: ents.extra.rate.gl.Filter) => {
        const result = ents.extra.rate.gl.Filter.ExplicitlyTyped.from(filter)
        // Treats empty statements the same as no filter at all
        return result.statement && !result.statement.filters.length
          ? {}
          : result
      },
    },
  )

  /**
   * Identifies this {@link ents.extra.rate.gl.Filter}
   *
   * __NOTE:__ this will be set internally on nested filters. You generally do
   * not need to input anything here
   */
  public readonly idSg = ngCore.input<string | undefined>(this.store.rootId, {
    alias: 'id',
  })

  /* --------------------------------- Outputs -------------------------------- */

  /**
   * Emits changes to this {@link ents.extra.rate.gl.Filter}
   */
  public readonly filterChange = ngCore.output<
    ents.extra.rate.gl.Filter | undefined
  >()

  /* -------------------------------- Computed -------------------------------- */

  /**
   * Depth of this {@link ents.extra.rate.gl.Filter} in the overall
   * {@link ents.extra.rate.gl.Filter}
   */
  public readonly depthSg = ngCore.computed((id = this.idSg()) =>
    this.store.getDepth(id),
  )

  /* --------------------------------- Methods -------------------------------- */

  /**
   * Creates a new filter
   */
  public createFilter() {
    this.filterChange.emit(this.store.defaultFilterSg())
  }

  /**
   * Adds a {@link ents.extra.rate.gl.Filter.Statement statement} when the
   * filter is a single {@link ents.extra.rate.gl.Filter.Comparison comparison}
   * upon clicking the add group button.
   */
  public addGroup() {
    const comparison = this.filterSg().comparison
    if (comparison) {
      const defaultFilter = this.store.defaultFilterSg()
      const group = {
        connector: ents.extra.rate.gl.Filter.Statement.Connector.Or,
        filters: [defaultFilter, defaultFilter],
      }
      this.filterChange.emit({
        connector: ents.extra.rate.gl.Filter.Statement.Connector.And,
        filters: [comparison, group],
      })
    }
  }

  /**
   * Initialization, primarily for root filters
   */
  public ngOnInit(): void {
    if (this.idSg() === this.store.rootId) {
      ngCore.runInInjectionContext(this._injector, () => {
        // If this is the root filter, we need to write it into the state whenever
        // it changes
        ngCore.effect(() => {
          this.store.patchState({ rootFilter: this.filterSg() })
        })

        // And we need to listen to changes to the root filter so we can emit them
        // here
        this.store.rootFilterChange$.subscribe(rootFilter => {
          this.filterChange.emit(ents.extra.rate.gl.Filter.from(rootFilter))
        })
      })
    }
  }
}
